import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MessageSquare, Paperclip, RotateCcw } from 'lucide-react'
import { useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { Breadcrumbs } from '../../components/ui/breadcrumbs'
import { Button } from '../../components/ui/button'
import { Callout, ErrorNote } from '../../components/ui/callout'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { EmptyState } from '../../components/ui/empty-state'
import { Skeleton, Textarea } from '../../components/ui/primitives'
import { RichText } from '../../components/ui/rich-text'
import { Timeline, TimelineItem } from '../../components/ui/timeline'
import { useToast } from '../../components/ui/toast'
import { api } from '../../lib/api'
import type {
  Attachment,
  TicketManagerApproval,
  TicketDetail as Ticket,
} from '../../lib/types'
import { formatDate } from '../../lib/ui'
import { PriorityBadge, StatusBadge } from '../components/TicketBits'

function AttachmentList({ files }: { files: Attachment[] }) {
  if (files.length === 0) return null
  return (
    <ul className="mt-2.5 space-y-1">
      {files.map((file) => (
        <li key={file.id} className="flex items-center gap-1.5">
          <Paperclip className="size-3 shrink-0 text-muted-foreground" aria-hidden />
          {/* Downloads go through the permission-checked view, never nginx. */}
          <a
            href={`/api/portal/attachments/${file.id}/download/`}
            className="text-xs text-primary underline underline-offset-2"
          >
            {file.filename}
          </a>
          <span className="text-xs text-muted-foreground">
            {Math.max(1, Math.round(file.size_bytes / 1024))} KB
          </span>
        </li>
      ))}
    </ul>
  )
}

function Csat({ ticket, onDone }: { ticket: Ticket; onDone: () => void }) {
  const [score, setScore] = useState<number | null>(null)
  const [comment, setComment] = useState('')
  const { toast } = useToast()

  const submit = useMutation({
    mutationFn: () => api.postJson(`/tickets/${ticket.ref}/csat/`, { score, comment }),
    onSuccess: () => {
      toast({ title: 'Thanks for the rating', variant: 'success' })
      onDone()
    },
  })

  if (ticket.csat_score !== null) {
    return (
      <Callout tone="info">Thanks — you rated this {ticket.csat_score} out of 5.</Callout>
    )
  }
  if (!ticket.can_rate) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle>How did we do?</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2" role="group" aria-label="Rating out of 5">
          {[1, 2, 3, 4, 5].map((value) => (
            <Button
              key={value}
              type="button"
              variant={score === value ? 'default' : 'outline'}
              size="icon"
              aria-pressed={score === value}
              onClick={() => setScore(value)}
            >
              {value}
            </Button>
          ))}
        </div>
        <Textarea
          rows={2}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          placeholder="Anything you would like to add? (optional)"
          aria-label="Rating comment"
          className="mt-3"
        />
        <Button
          size="sm"
          className="mt-2"
          disabled={score === null || submit.isPending}
          onClick={() => submit.mutate()}
        >
          Send rating
        </Button>
        {submit.error && <ErrorNote className="mt-2" message={(submit.error as Error).message} />}
      </CardContent>
    </Card>
  )
}

/** Why this request has not moved.
 *
 * A request waiting on a manager looks identical to one the desk has ignored,
 * and the second reading is the one people act on — they chase IT. So the gate
 * is stated where the requester is already looking, with the name of the person
 * holding it.
 *
 * Renders nothing when there is no approval, which is most requests. A banner
 * saying "no approval required" on every ticket would be noise that trains
 * people to skip the one that matters.
 */
function ApprovalNotice({ approval }: { approval: TicketManagerApproval | null }) {
  if (!approval) return null

  if (approval.status === 'pending') {
    return (
      <Callout tone="warning" className="mt-3">
        <p className="font-medium">Waiting for {approval.approver_name} to approve</p>
        <p className="mt-0.5 opacity-90">
          Requested {formatDate(approval.requested_at)}. The service desk cannot start on this
          until it is approved. {approval.approver_email} has been emailed.
        </p>
      </Callout>
    )
  }

  if (approval.status === 'approved') {
    return (
      <Callout tone="info" className="mt-3">
        Approved by {approval.approver_name} on {formatDate(approval.decided_at)}.
        {approval.comment && ` “${approval.comment}”`}
      </Callout>
    )
  }

  // Rejected, cancelled or expired. All three mean the request is not going
  // ahead, and the reason is the only thing worth reading.
  return (
    <Callout tone="critical" className="mt-3">
      <p className="font-medium">{approval.status_label}</p>
      {approval.comment ? (
        <p className="mt-0.5 opacity-90">
          {approval.approver_name}: “{approval.comment}”
        </p>
      ) : (
        <p className="mt-0.5 opacity-90">
          No reason was recorded. Ask {approval.approver_name} if you need this.
        </p>
      )}
    </Callout>
  )
}

export function TicketDetailPage() {
  const { ref } = useParams<{ ref: string }>()
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)
  const [reply, setReply] = useState('')
  const { toast } = useToast()

  const { data, isLoading, error } = useQuery({
    queryKey: ['ticket', ref],
    queryFn: () => api.get<Ticket>(`/tickets/${ref}/`),
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['ticket', ref] })
    void queryClient.invalidateQueries({ queryKey: ['tickets'] })
  }

  const sendReply = useMutation({
    mutationFn: () => api.postJson(`/tickets/${ref}/case-logs/`, { body_html: reply }),
    onSuccess: () => {
      setReply('')
      toast({ title: 'Reply sent', description: 'The service desk has been notified.' })
      refresh()
    },
  })

  const uploadFile = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData()
      form.set('file', file)
      return api.postForm(`/tickets/${ref}/attachments/`, form)
    },
    onSuccess: () => {
      if (fileInput.current) fileInput.current.value = ''
      toast({ title: 'File attached', variant: 'success' })
      refresh()
    },
  })

  const reopen = useMutation({
    mutationFn: () => api.postJson(`/tickets/${ref}/reopen/`, {}),
    onSuccess: () => {
      toast({ title: 'Ticket reopened' })
      refresh()
    },
  })

  if (isLoading) {
    return (
      <div className="max-w-3xl space-y-3">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="max-w-3xl space-y-3">
        <ErrorNote message="That ticket is not available." />
        <Link to="/tickets" className="text-sm text-primary hover:underline">
          ← My tickets
        </Link>
      </div>
    )
  }

  const formEntries = Object.entries(data.form_data ?? {})

  return (
    <div className="max-w-3xl">
      <Breadcrumbs
        className="mb-3"
        items={[{ label: 'My tickets', to: '/tickets' }, { label: data.ref }]}
      />

      <div className="flex flex-wrap items-center gap-2.5">
        <span className="font-mono text-sm text-muted-foreground">{data.ref}</span>
        <StatusBadge status={data.status} />
        <PriorityBadge priority={data.priority} />
      </div>
      <h1 className="mt-1.5 text-lg font-semibold tracking-tight">{data.title}</h1>
      <p className="mt-0.5 text-sm text-muted-foreground">
        {data.service ? `${data.service.service} / ${data.service.subcategory} · ` : ''}
        Raised {formatDate(data.created_at)}
      </p>

      <ApprovalNotice approval={data.manager_approval} />

      <Card className="mt-4">
        <CardContent className="pt-4">
          {data.description ? (
            <RichText html={data.description} />
          ) : (
            <p className="text-sm text-muted-foreground">No description given.</p>
          )}
          {formEntries.length > 0 && (
            <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 border-t border-border pt-3 text-sm">
              {formEntries.map(([key, value]) => (
                <div key={key} className="contents">
                  <dt className="text-muted-foreground">{key}</dt>
                  <dd>{String(value)}</dd>
                </div>
              ))}
            </dl>
          )}
          <AttachmentList files={data.attachments} />
        </CardContent>
      </Card>

      <section className="mt-5">
        <h2 className="mb-2 text-sm font-semibold tracking-tight">Conversation</h2>
        {data.case_logs.length === 0 ? (
          <Card>
            <EmptyState
              bare
              icon={MessageSquare}
              title="Nothing yet"
              description="Replies from the service desk will appear here."
            />
          </Card>
        ) : (
          <Timeline>
            {data.case_logs.map((entry) => (
              <TimelineItem
                key={entry.id}
                icon={MessageSquare}
                title={entry.author_name}
                meta={formatDate(entry.created_at)}
              >
                <RichText html={entry.body_html} />
                <AttachmentList files={entry.attachments} />
              </TimelineItem>
            ))}
          </Timeline>
        )}
      </section>

      <div className="mt-5 space-y-4">
        <Csat ticket={data} onDone={refresh} />

        {data.can_reopen && (
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm">
                This ticket is finished. If the problem came back, you can reopen it.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2.5"
                onClick={() => reopen.mutate()}
                disabled={reopen.isPending}
              >
                <RotateCcw />
                Reopen this ticket
              </Button>
              {reopen.error && (
                <ErrorNote className="mt-2" message={(reopen.error as Error).message} />
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Reply</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              rows={4}
              value={reply}
              onChange={(event) => setReply(event.target.value)}
              placeholder="Add a message for the service desk"
              aria-label="Reply to the service desk"
            />
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button
                size="sm"
                onClick={() => sendReply.mutate()}
                disabled={!reply.trim() || sendReply.isPending}
              >
                {sendReply.isPending ? 'Sending…' : 'Send reply'}
              </Button>
              <input
                ref={fileInput}
                type="file"
                aria-label="Attach a file"
                className="text-sm text-muted-foreground file:mr-2 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1 file:text-sm file:text-foreground"
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  if (file) uploadFile.mutate(file)
                }}
              />
              {uploadFile.isPending && (
                <span className="text-xs text-muted-foreground">Uploading…</span>
              )}
            </div>
            {(sendReply.error || uploadFile.error) && (
              <ErrorNote
                className="mt-3"
                message={((sendReply.error ?? uploadFile.error) as Error).message}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
